import {
  Box,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { format, formatDistanceToNow } from "date-fns";
import enLocale from "date-fns/locale/en-US";
import DOMPurify from "isomorphic-dompurify";
import { useEffect, useMemo, useState } from "react";
import { CrossIcon, UserCheckIcon } from "@/components/common/icons";
import { TIME_FORMATS } from "@/config";
import { GREY, VIOLET } from "@/config/mui/colorPalette";

type InAppNotificationProps = {
  message: string;
  date: Date;
  onClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  sx?: SxProps<Theme>;
};

const formatDistanceLocale = {
  lessThanXMinutes: "Just now",
  xMinutes: "{{count}} min(s) ago",
  aboutXHours: "{{count}} hour(s) ago",
  xHours: "{{count}} hour(s) ago",
};

const customDistanceLocale = {
  formatDistance: (token: string, count: number, options: unknown) => {
    if (token in formatDistanceLocale) {
      return formatDistanceLocale[token].replace("{{count}}", count);
    }
    return enLocale.formatDistance(token, count, options);
  },
};

const formatNotificationDateToNow = (date: Date) =>
  formatDistanceToNow(date, {
    locale: customDistanceLocale,
  });

const DATE_TO_NOW_UPDATE_INTERVAL = 1000 * 60; // 1 minute

export const InAppNotification = ({
  message,
  date,
  onClose,
  sx,
}: InAppNotificationProps) => {
  const sanitizedMessage = useMemo(
    () =>
      DOMPurify.sanitize(message, {
        ALLOWED_TAGS: ["b", "i", "em", "strong", "p", "br"],
      }),
    [message]
  );

  const formattedDate = useMemo(
    () => format(date, TIME_FORMATS.COMPACT_SPACE),
    [date]
  );

  const [formattedDateToNow, setFormattedDateToNow] = useState(
    formatNotificationDateToNow(date)
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setFormattedDateToNow(formatNotificationDateToNow(date));
    }, DATE_TO_NOW_UPDATE_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [date]);

  return (
    <Stack
      gap={1}
      direction="row"
      justifyContent="stretch"
      sx={{
        width: "100%",
        gap: 1.5,
        ...sx,
      }}
    >
      <Box
        sx={{
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          minHeight: "40px",
          minWidth: "40px",
          bgcolor: VIOLET[40],
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <UserCheckIcon color={VIOLET[90]} size={24} />
      </Box>
      <Stack gap={0.25} width="100%">
        <Typography
          variant="paragraphSmall"
          color="text.primary"
          dangerouslySetInnerHTML={{
            __html: sanitizedMessage,
          }}
        />
        <Typography
          variant="paragraphSmall"
          color="text.secondary"
          sx={{
            whiteSpace: "pre",
          }}
        >
          {formattedDateToNow} • {formattedDate}
        </Typography>
      </Stack>
      <IconButton
        sx={{
          p: 0,
          width: "20px",
          height: "20px",
          minHeight: "20px",
          minWidth: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        aria-label="Close notification"
        onClick={onClose}
      >
        <CrossIcon color={GREY[50]} />
      </IconButton>
    </Stack>
  );
};
